/**
 * Clock estimation.
 *
 * Two machines never agree on the time. Their wall clocks differ by anything
 * from milliseconds to minutes, and both drift. Since every synchronization
 * decision in CineSync is expressed in *server time*, the first job of a client
 * is to work out how far its own clock sits from the relay's.
 *
 * This is the NTP exchange, reduced to its essentials:
 *
 *     t0  client sends a probe        (client clock)
 *     t1  relay receives it           (server clock)
 *     t2  relay replies               (server clock)
 *     t3  client receives the reply   (client clock)
 *
 *     offset = ((t1 - t0) + (t2 - t3)) / 2
 *     rtt    = (t3 - t0) - (t2 - t1)
 *
 * `offset` is what you add to a client timestamp to get a server timestamp. It
 * is exact only when the network delay is symmetric, which it never quite is,
 * so a single sample is not trustworthy. The error in a sample is bounded by
 * rtt/2, which gives us a useful rule: **low round-trip samples are better
 * samples**. The estimator therefore keeps a window of recent probes and
 * believes only the fastest quartile of them.
 */

/** One completed round-trip probe. All four timestamps are milliseconds. */
export interface ClockSample {
  /** Client clock when the probe was sent. */
  readonly t0: number;
  /** Server clock when the probe arrived. */
  readonly t1: number;
  /** Server clock when the reply was sent. */
  readonly t2: number;
  /** Client clock when the reply arrived. */
  readonly t3: number;
}

/** The estimator's current belief about the relationship between the clocks. */
export interface ClockEstimate {
  /** Add this to a client timestamp to obtain a server timestamp. */
  readonly offsetMs: number;
  /** Round-trip time of the best sample currently informing the estimate. */
  readonly rttMs: number;
  /**
   * Spread of the offsets that were averaged together, in milliseconds. A
   * small number means the samples agree and the estimate is trustworthy; a
   * large one means the network is jittering and sync should be treated with
   * suspicion.
   */
  readonly uncertaintyMs: number;
  /** How many samples are currently in the window. */
  readonly sampleCount: number;
}

export interface ClockEstimatorOptions {
  /** How many recent samples to retain. */
  readonly windowSize: number;
  /**
   * Samples slower than this are discarded outright. A probe that took two
   * seconds tells us almost nothing about the offset and would only add noise.
   */
  readonly maxAcceptableRttMs: number;
  /**
   * Fraction of the window, ordered by round-trip time, that contributes to
   * the estimate. 0.25 keeps the fastest quarter.
   */
  readonly bestFraction: number;
}

export const DEFAULT_CLOCK_OPTIONS: ClockEstimatorOptions = {
  windowSize: 16,
  maxAcceptableRttMs: 2_000,
  bestFraction: 0.25,
};

interface ScoredSample {
  readonly offsetMs: number;
  readonly rttMs: number;
}

/**
 * Derive offset and round-trip time from a raw probe.
 *
 * Exported because it is meaningful on its own, and because testing it
 * directly is clearer than testing it through the estimator.
 */
export function scoreSample(sample: ClockSample): ScoredSample {
  const { t0, t1, t2, t3 } = sample;
  return {
    offsetMs: (t1 - t0 + (t2 - t3)) / 2,
    rttMs: t3 - t0 - (t2 - t1),
  };
}

/**
 * Maintains a running estimate of the offset between the local clock and the
 * relay's clock.
 *
 * The estimator holds no notion of "now" and calls no time function itself.
 * Every timestamp arrives as an argument, which keeps it deterministic and
 * makes its behaviour over hours of drift straightforward to test.
 */
export class ClockEstimator {
  readonly #options: ClockEstimatorOptions;
  readonly #samples: ScoredSample[] = [];
  #estimate: ClockEstimate | null = null;

  constructor(options: Partial<ClockEstimatorOptions> = {}) {
    this.#options = { ...DEFAULT_CLOCK_OPTIONS, ...options };
  }

  /**
   * Feed a completed probe into the estimator.
   *
   * @returns `true` if the sample was accepted, `false` if it was rejected as
   *   implausible. Rejections are worth surfacing in diagnostics: a burst of
   *   them means the connection has degraded.
   */
  addSample(sample: ClockSample): boolean {
    const scored = scoreSample(sample);

    // A negative round trip is physically impossible and means one of the two
    // clocks jumped mid-probe, typically an NTP correction on the client.
    if (!Number.isFinite(scored.offsetMs) || !Number.isFinite(scored.rttMs)) return false;
    if (scored.rttMs < 0) return false;
    if (scored.rttMs > this.#options.maxAcceptableRttMs) return false;

    this.#samples.push(scored);
    if (this.#samples.length > this.#options.windowSize) this.#samples.shift();
    this.#estimate = this.#recompute();
    return true;
  }

  /** The current belief, or `null` until at least one sample has landed. */
  get estimate(): ClockEstimate | null {
    return this.#estimate;
  }

  /** True once the estimate is good enough to start a synchronized session. */
  isConverged(maxUncertaintyMs = 50, minSamples = 4): boolean {
    const e = this.#estimate;
    return e !== null && e.sampleCount >= minSamples && e.uncertaintyMs <= maxUncertaintyMs;
  }

  /** Convert a local timestamp to the relay's frame of reference. */
  toServerTime(clientMs: number): number {
    return clientMs + (this.#estimate?.offsetMs ?? 0);
  }

  /** Convert a relay timestamp back into the local frame of reference. */
  toClientTime(serverMs: number): number {
    return serverMs - (this.#estimate?.offsetMs ?? 0);
  }

  /**
   * Discard all history. Call this after a reconnection: the relay may be a
   * different edge location with a different clock, so old samples are worse
   * than no samples.
   */
  reset(): void {
    this.#samples.length = 0;
    this.#estimate = null;
  }

  #recompute(): ClockEstimate {
    // Fastest samples first. The fastest probe had the least room to hide an
    // asymmetric delay, so its offset is the most credible.
    const ordered = [...this.#samples].sort((a, b) => a.rttMs - b.rttMs);
    const keep = Math.max(1, Math.ceil(ordered.length * this.#options.bestFraction));
    const best = ordered.slice(0, keep);

    let offsetSum = 0;
    let minOffset = Number.POSITIVE_INFINITY;
    let maxOffset = Number.NEGATIVE_INFINITY;
    for (const s of best) {
      offsetSum += s.offsetMs;
      if (s.offsetMs < minOffset) minOffset = s.offsetMs;
      if (s.offsetMs > maxOffset) maxOffset = s.offsetMs;
    }

    return {
      offsetMs: offsetSum / best.length,
      // `best` is non-empty by construction, so index 0 always exists.
      rttMs: best[0]!.rttMs,
      uncertaintyMs: best.length > 1 ? maxOffset - minOffset : best[0]!.rttMs / 2,
      sampleCount: this.#samples.length,
    };
  }
}
